import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";

export type DeployServer = { id: string; label: string };

export async function listDeployServers(): Promise<DeployServer[]> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/servers`, { headers: { Accept: "application/json" }, credentials: "include" });
  } catch (e) { throw new Error(`network error: ${(e as Error).message}`); }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return ((await res.json()) as { servers: DeployServer[] }).servers;
}

// --- Admin: deploy-server management (credentials live in the DB) ----------

export type AdminDeployServer = {
  id: string;
  label: string;
  host: string;
  port: number;
  username: string;
  remoteDir: string;
  protocol: "sftp" | "ftp" | "ftps";
  allowInsecure: boolean;
  hostKeySha256?: string;
  createdAt: string;
  updatedAt: string;
};

/** Fields the form submits. `password` is write-only — omit on edit to keep it. */
export type DeployServerInput = {
  id: string;
  label: string;
  host: string;
  port: number;
  username: string;
  password?: string;
  remoteDir: string;
  protocol?: "sftp" | "ftp" | "ftps";
  allowInsecure?: boolean;
  hostKeySha256?: string;
};

async function parseErr(res: Response): Promise<string> {
  let msg = `http ${res.status}`;
  try {
    const j = (await res.clone().json()) as { error?: string; message?: string };
    msg = j.message || j.error || msg;
  } catch { /* not json */ }
  return msg;
}

/** Admin list — full records (no secret). Requires the caller to be an admin. */
export async function listDeployServersAdmin(): Promise<AdminDeployServer[]> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/servers`, { headers: { Accept: "application/json" }, credentials: "include" });
  } catch (e) { throw new Error(`network error: ${(e as Error).message}`); }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, await parseErr(res));
  const j = (await res.json()) as { servers: AdminDeployServer[]; admin?: boolean };
  return j.servers;
}

export async function createDeployServer(input: DeployServerInput): Promise<AdminDeployServer> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/servers`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    });
  } catch (e) { throw new Error(`network error: ${(e as Error).message}`); }
  if (!res.ok) throw new HttpError(res.status, await parseErr(res));
  return ((await res.json()) as { server: AdminDeployServer }).server;
}

export async function updateDeployServer(
  id: string,
  patch: Partial<Omit<DeployServerInput, "id">>,
): Promise<AdminDeployServer> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/servers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(patch),
    });
  } catch (e) { throw new Error(`network error: ${(e as Error).message}`); }
  if (!res.ok) throw new HttpError(res.status, await parseErr(res));
  return ((await res.json()) as { server: AdminDeployServer }).server;
}

export async function deleteDeployServer(id: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/servers/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
  } catch (e) { throw new Error(`network error: ${(e as Error).message}`); }
  if (!res.ok) throw new HttpError(res.status, await parseErr(res));
}

export type DeployResult = { server: string; serverLabel: string; jar: string; commit: string; bytes: number; remotePath: string };

export async function deployPlugin(project: string, branch: string, server: string): Promise<DeployResult> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/deploy/plugin`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ project, branch: branch.trim() || undefined, server }),
    });
  } catch (e) { throw new Error(`network error: ${(e as Error).message}`); }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (res.status === 403) throw new HttpError(403, "forbidden: no access to this project");
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try { const j = (await res.clone().json()) as { error?: string; message?: string }; msg = j.message || j.error || msg; } catch { /* not json */ }
    throw new HttpError(res.status, msg);
  }
  return (await res.json()) as DeployResult;
}
